import type { TaskAssignee } from '@einwert/valuation-api-client'
import {
    OrderDetailsLoader,
    TaskAttachmentsLoader,
    TaskChangeLogLoader,
    TaskCommentsLoader,
    TaskDetailsDrawer,
    TaskDetailsLoader,
    TaskHeaderLoader,
    type OrderStatus,
    tokens,
} from 'ComponentLibrary'
import { TaskHeader } from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/TaskHeader/TaskHeader'
import { useTaskDetails } from 'ClientPortal/src/pages/SideDrawers/useTaskDetails'
import { TaskAttachments } from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/TaskAttachments/TaskAttachments'
import { OrderDetails } from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/OrderDetails/OrderDetails'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    TaskLogSimplified,
    useGetOrderWithAssetOfFinalisedValuation,
    useTaskChangeLogCreatedLastClosed,
} from 'ClientPortal/src/pages/SideDrawers/TaskSideDrawer.data'
import invariant from 'tiny-invariant'
import { TaskChangeLog } from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/TaskChangeLog/TaskChangeLog'
import { TaskDetails } from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/TaskDetails/TaskDetails'
import { some } from 'lodash'
import { TaskSelectionOnChange } from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/TaskDetails/TaskAssigneeSelection'
import { useGetThreads } from 'ClientPortal/src/api/Queries/threads'
import { Stack } from '@mui/material'
import { TaskSideDrawerMessageList } from 'ClientPortal/src/pages/Orders/OrderDetails/ThreadList/MessageList/TaskSideDrawerMessageList'
import { FormTaskTitle } from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/TaskHeader/FormTaskTitle'
import { FormTaskDetails } from 'ComponentLibrary/src/widgets/TaskDetailsDrawer/TaskDetails/FormTaskDetails'
import { PencilSimpleIcon } from '@phosphor-icons/react'
import { Task } from 'ComponentLibrary/src/common/tasks'
import { useGetAllPortfoliosValueOverview } from 'ClientPortal/src/api/Queries/value-overview'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { useGetTask } from 'ClientPortal/src/api/Queries/tasks'
import { throwOnForbiddenApiError } from 'ClientPortal/src/api/react-query-config'

import { useGetAppraiserUsers } from 'ClientPortal/src/api/Queries/users'
import { useGetOrderOverviewByOrderId } from 'ClientPortal/src/api/Queries/orders'
import { useTranslation } from 'react-i18next'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'
import { useDocumentTagOptions } from 'ClientPortal/src/i18n/useDocumentTagOptions'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type TaskSideDrawerProps = { taskId: string | undefined; onCloseDrawer: () => void }

export const TaskSideDrawer = ({ taskId, onCloseDrawer }: TaskSideDrawerProps) => {
    const [isTaskTitleEditing, setIsTaskTitleEditing] = useState(false)

    const handleStartTaskTitleEditing = () => {
        setIsTaskTitleEditing(true)
    }

    const handleFinishTaskTitleEditing = () => {
        setIsTaskTitleEditing(false)
    }

    useEffect(() => {
        if (taskId === undefined) {
            setIsTaskTitleEditing(false)
        }
    }, [setIsTaskTitleEditing, taskId])

    return (
        <TaskDetailsDrawer
            isOpen={taskId !== undefined}
            handleCloseDrawer={onCloseDrawer}
            showCloseDrawerButton={!isTaskTitleEditing}
            header={
                taskId && (
                    <TaskHeaderWrapper
                        taskId={taskId}
                        isTaskTitleEditing={isTaskTitleEditing}
                        onStartEditing={handleStartTaskTitleEditing}
                        onFinishEditing={handleFinishTaskTitleEditing}
                    />
                )
            }
            orderDetailsComponent={taskId && <OrderDetailsWrapper taskId={taskId} onCloseDrawer={onCloseDrawer} />}
            taskChangeLogComponent={taskId && <TaskChangeLogWrapper taskId={taskId} />}
            taskDetailsComponent={taskId && <TaskDetailsWrapper taskId={taskId} />}
            taskDocumentsComponent={taskId && <TaskDocumentsWrapper taskId={taskId} />}
            taskThreadComponent={taskId && <TaskThreadWrapper taskId={taskId} />}
        />
    )
}

type WithTaskDetailsProps = {
    taskId: string
}

type TaskHeaderWrapperProps = WithTaskDetailsProps & {
    isTaskTitleEditing: boolean
    onStartEditing: () => void
    onFinishEditing: () => void
}

function TaskHeaderWrapper({ taskId, isTaskTitleEditing, onStartEditing, onFinishEditing }: TaskHeaderWrapperProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'taskSideDrawer' })
    const { taskManagement, taskData, isPending } = useTaskDetails({ taskId })
    const formTaskTitleCopy = {
        updateErrorMessage: t('header.form.updateErrorMessage'),
        cancelAriaLabel: t('common.cancelAriaLabel'),
        submitAriaLabel: t('common.submitAriaLabel'),
    }
    const taskHeaderCopy = {
        editTitleAriaLabel: t('header.editTitleAriaLabel'),
    }

    if (isPending) {
        return <TaskHeaderLoader />
    }

    invariant(taskData, 'taskData must be defined')

    const handleSubmit = (updatedTask: Pick<Task, 'title'>) => {
        taskManagement.updateTask(updatedTask)
        onFinishEditing()
    }

    const handleToggleStatus = () => taskManagement.updateTask({ status: taskData.status === 'done' ? 'open' : 'done' })

    return (
        <>
            {isTaskTitleEditing ? (
                <FormTaskTitle
                    task={taskData}
                    isMutatingTask={taskManagement.isMutatingTask}
                    onSubmit={handleSubmit}
                    onCancel={onFinishEditing}
                    onToggleStatus={handleToggleStatus}
                    copy={formTaskTitleCopy}
                />
            ) : (
                <TaskHeader
                    title={taskData.title}
                    isDone={taskData.status === 'done'}
                    toggleStatus={handleToggleStatus}
                    onEdit={onStartEditing}
                    copy={taskHeaderCopy}
                />
            )}
        </>
    )
}

function OrderDetailsWrapper({ taskId, onCloseDrawer }: { taskId: UUID; onCloseDrawer: () => void }) {
    const { t } = useTranslation('pages', { keyPrefix: 'taskSideDrawer.orderDetails' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const locale = useCurrentResolvedLocale()
    const { taskData, isTaskPending } = useGetTask({ taskId, throwOnError: throwOnForbiddenApiError })
    const { orderOverview, isOrderOverviewPending, singleAssetValueOverview, isSingleAssetValueOverviewPending } =
        useGetOrderWithAssetOfFinalisedValuation(taskData?.order_uuid)
    const { multiPortfolioValueOverview, isMultiPortfolioValueOverviewPending } = useGetAllPortfoliosValueOverview()
    const { appraiserUsers } = useGetAppraiserUsers({ orderId: taskData?.order_uuid, enabled: !!taskData?.order_uuid })
    const userLinkCopy = useUserLinkCopy()
    const orderDetailsCopy = {
        title: t('title'),
        orderName: t('orderName'),
        valuationDate: t('valuationDate'),
        orderStatus: t('orderStatus'),
        appraiser: t('appraiser'),
        asset: t('asset'),
        portfolio: t('portfolio'),
        portfolios: t('portfolios'),
        orderStatusLabel: (status: OrderStatus) => tTerminology(`orderStatuses.${status}.label`),
        appraiserAssignment: {
            addButton: t('appraiserAssignment.addButton'),
            modal: {
                customOptionName: t('appraiserAssignment.modal.customOptionName'),
                customOptionGroup: t('appraiserAssignment.modal.customOptionGroup'),
                title: t('appraiserAssignment.modal.title'),
                cancel: t('appraiserAssignment.modal.cancel'),
                duplicateWarning: t('appraiserAssignment.modal.duplicateWarning'),
                customInfo: t('appraiserAssignment.modal.customInfo'),
                mainAppraiser: t('appraiserAssignment.modal.mainAppraiser'),
                secondaryAppraiser: t('appraiserAssignment.modal.secondaryAppraiser'),
                tertiaryAppraiser: t('appraiserAssignment.modal.tertiaryAppraiser'),
                nameAndCertification: t('appraiserAssignment.modal.nameAndCertification'),
                remove: t('appraiserAssignment.modal.remove'),
                addSecondary: t('appraiserAssignment.modal.addSecondary'),
                addTertiary: t('appraiserAssignment.modal.addTertiary'),
            },
        },
    }

    const navigate = useNavigate()
    const handleOrderClicked = useCallback(() => {
        if (orderOverview === undefined) {
            return
        }

        void navigate(`/orders/${orderOverview.uuid}/overview`)
        onCloseDrawer()
    }, [navigate, orderOverview, onCloseDrawer])

    const handleAssetClicked = useCallback(() => {
        if (singleAssetValueOverview === undefined) {
            return
        }

        void navigate(`/assets/${singleAssetValueOverview.uuid}/`)
        onCloseDrawer()
    }, [navigate, singleAssetValueOverview, onCloseDrawer])

    if (isOrderOverviewPending || isMultiPortfolioValueOverviewPending || isTaskPending) {
        return <OrderDetailsLoader />
    }

    invariant(orderOverview, 'orderOverview must be defined')

    const portfolios = multiPortfolioValueOverview?.portfolios
        .filter(
            (portfolio) =>
                some(portfolio.assets, (asset) => asset.uuid === singleAssetValueOverview?.uuid) && portfolio.type !== 'synthetic'
        )
        .map((portfolio) => portfolio.title)

    return (
        <OrderDetails
            assetDetails={
                singleAssetValueOverview && {
                    assetAddress: singleAssetValueOverview.address,
                    onAssetLinkClicked: handleAssetClicked,
                }
            }
            shouldIncludeAsset={orderOverview?.valuation_status === 'finalised' && isSingleAssetValueOverviewPending}
            appraiserAssignment={orderOverview.appraiser_assignment}
            appraiserUsers={appraiserUsers}
            orderAddress={orderOverview.address}
            onOrderLinkClicked={handleOrderClicked}
            status={orderOverview.status}
            valuationDate={orderOverview.valuation_date}
            portfolios={portfolios}
            copy={orderDetailsCopy}
            userLinkCopy={userLinkCopy}
            locale={locale}
        />
    )
}

const getUserAndTimeFromChangeLog = (changeLog: TaskLogSimplified) => {
    return { time: changeLog.timestamp, user: changeLog.author_user }
}

function TaskChangeLogWrapper({ taskId }: WithTaskDetailsProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'taskSideDrawer.changeLog' })
    const locale = useCurrentResolvedLocale()
    const { taskCreatedLog, taskLastClosedLog, isPending } = useTaskChangeLogCreatedLastClosed(taskId)
    const changeLogCopy = {
        todayAt: (time: string) => t('todayAt', { time }),
        onDate: (date: string) => t('onDate', { date }),
        createdBy: (userName: string, dateOrTime: string) => t('createdBy', { userName, dateOrTime }),
        createdAutomatically: (dateOrTime: string) => t('createdAutomatically', { dateOrTime }),
        closedBy: (userName: string, dateOrTime: string) => t('closedBy', { userName, dateOrTime }),
        closedAutomatically: (dateOrTime: string) => t('closedAutomatically', { dateOrTime }),
    }

    if (isPending) {
        return <TaskChangeLogLoader />
    }

    return (
        <TaskChangeLog
            created={taskCreatedLog && getUserAndTimeFromChangeLog(taskCreatedLog)}
            closed={taskLastClosedLog && getUserAndTimeFromChangeLog(taskLastClosedLog)}
            copy={changeLogCopy}
            locale={locale}
        />
    )
}

function TaskDetailsWrapper({ taskId }: WithTaskDetailsProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'taskSideDrawer.details' })
    const locale = useCurrentResolvedLocale()
    const { taskData, taskManagement, isPending, users, isUsersInitialLoading } = useTaskDetails({ taskId })
    const [isEditing, setIsEditing] = useState(false)

    const taskAssignee = taskData?.assignee ?? null
    const userLinkCopy = useUserLinkCopy()
    const taskDetailsCopy = {
        title: t('title'),
        assignee: t('assignee'),
        customerVisibility: t('customerVisibility'),
        hidden: t('hidden'),
        visible: t('visible'),
        taskType: t('taskType'),
        deadline: t('deadline'),
        notSet: t('notSet'),
        description: t('description'),
        showLess: t('showLess'),
        showMore: t('showMore'),
    }
    const formTaskDetailsCopy = {
        title: t('title'),
        assignee: t('assignee'),
        updateErrorMessage: t('form.updateErrorMessage'),
        updateErrorTitle: t('form.updateErrorTitle'),
        hidden: t('hidden'),
        hiddenSecondary: t('form.hiddenSecondary'),
        deadline: t('deadline'),
        description: t('description'),
        cancelAriaLabel: t('form.cancelAriaLabel'),
        submitAriaLabel: t('form.submitAriaLabel'),
    }

    const handleAssigneeChange: TaskSelectionOnChange = useCallback(
        (selectedAssignee: TaskAssignee | null) => {
            taskManagement.updateTask({ assignee: selectedAssignee !== null ? selectedAssignee : undefined })
        },
        [taskManagement]
    )

    if (isPending || isUsersInitialLoading) {
        return <TaskDetailsLoader />
    }

    invariant(taskData, 'taskData must be defined')

    const handleSubmit = (updatedTask: Pick<Task, 'description' | 'due_date'>) => {
        taskManagement.updateTask(updatedTask)
        setIsEditing(false)
    }

    return (
        <>
            {isEditing ? (
                <FormTaskDetails
                    assigneeOptions={users}
                    onChangeAssignee={handleAssigneeChange}
                    selectedAssignee={taskAssignee}
                    task={taskData}
                    isMutatingTask={taskManagement.isMutatingTask}
                    onSubmit={handleSubmit}
                    onCancel={() => setIsEditing(false)}
                    copy={formTaskDetailsCopy}
                    userLinkCopy={userLinkCopy}
                    locale={locale}
                />
            ) : (
                <TaskDetails
                    assigneeOptions={users}
                    onChangeAssignee={handleAssigneeChange}
                    selectedAssignee={taskAssignee}
                    task={taskData}
                    actions={[
                        {
                            icon: <PencilSimpleIcon size={tokens.SizingIconS} color={tokens.ColorIconBoldest} weight="bold" />,
                            onClick: () => setIsEditing(true),
                        },
                    ]}
                    copy={taskDetailsCopy}
                    userLinkCopy={userLinkCopy}
                    locale={locale}
                />
            )}
        </>
    )
}

function TaskDocumentsWrapper({ taskId }: WithTaskDetailsProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'taskSideDrawer.attachments' })
    const documentTagOptions = useDocumentTagOptions()
    const { taskData, fileManagement, generateFilesZipUrl } = useTaskDetails({ taskId })
    const { orderOverview, isOrderOverviewPending } = useGetOrderOverviewByOrderId(taskData?.order_uuid)
    const attachmentsCopy = {
        title: t('title'),
        downloadAllAriaLabel: t('downloadAllAriaLabel'),
        editDocumentsAriaLabel: t('editDocumentsAriaLabel'),
        uploadDocumentsAriaLabel: t('uploadDocumentsAriaLabel'),
        item: {
            edit: t('item.edit'),
            delete: t('item.delete'),
            optionsAriaLabel: t('item.optionsAriaLabel'),
        },
    }
    const fileUploadModalCopy = {
        uploadTitle: t('modal.uploadTitle'),
        editTitle: t('modal.editTitle'),
        createButton: t('modal.createButton'),
        confirmButton: t('modal.confirmButton'),
        cancelButton: t('modal.cancelButton'),
        dropzonePrompt: t('modal.dropzonePrompt'),
        nameLabel: t('modal.nameLabel'),
        tagsLabel: t('modal.tagsLabel'),
        unknownMimeTypeLabel: t('modal.unknownMimeTypeLabel'),
        uploadErrorAlert: {
            title: t('modal.uploadErrorAlert.title'),
            description: t('modal.uploadErrorAlert.description'),
            supportText: t('modal.uploadErrorAlert.supportText'),
        },
    }

    if (fileManagement.isDocumentsPending || isOrderOverviewPending) {
        return <TaskAttachmentsLoader />
    }

    const isExternalMailboxOrder = !!orderOverview?.external_participant_mailbox?.proxy_email

    return (
        <TaskAttachments
            documents={fileManagement.documents}
            updateDocuments={fileManagement.updateDocuments}
            isUpdatingDocuments={fileManagement.isUpdatingDocuments}
            onUploadFiles={fileManagement.uploadDocuments}
            failedDocumentUploads={fileManagement.failedDocumentUploads}
            isUploadingDocuments={fileManagement.isUploadingDocuments}
            downloadDocument={fileManagement.downloadDocument}
            onDownloadAll={() => generateFilesZipUrl({ taskId })}
            removeDocument={fileManagement.deleteDocument}
            isDownloadingDocument={fileManagement.isDownloadingDocument}
            isUploadDisabled={isExternalMailboxOrder}
            uploadDisabledTooltip={t('uploadDisabledTooltip')}
            copy={attachmentsCopy}
            fileUploadModalCopy={fileUploadModalCopy}
            fileUploadModalTagOptions={documentTagOptions}
        />
    )
}

function TaskThreadWrapper({ taskId }: { taskId: string }) {
    const { taskData, isPending: isTaskPending } = useTaskDetails({ taskId })
    const { threads, isThreadsPending } = useGetThreads({ orderId: taskData?.order_uuid })

    if (isTaskPending || isThreadsPending || threads.length === 0) {
        return <TaskCommentsLoader />
    }

    const taskThread = threads.filter((thread) => thread.uuid === taskData?.communication_thread)

    if (taskThread.length === 0) {
        return null
    }

    return (
        <Stack
            direction="row"
            sx={{
                width: '100%',
                height: '100%',
            }}
        >
            <TaskSideDrawerMessageList thread={taskThread[0]} />
        </Stack>
    )
}
